import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, Info, Loader2, X } from 'lucide-react';
import { Button } from './Button';
import { useNotice } from './Notice';
import { apiError } from '../../lib/utils';

type StepState = 'wait' | 'active' | 'done' | 'failed';
type Outcome = 'sent' | 'failed' | 'pending';

interface Step { id: string; label: string; detail?: string; state: StepState }

export interface ActivityInput {
  title: string;
  subtitle: string;
  dockTitle: string;
  to?: string;
  attachments?: string;
  revision?: string;
  kindLabel?: string;
  confirmed?: boolean;
  steps: { id: string; label: string; detail?: string }[];
}

interface Job extends ActivityInput {
  id: number;
  steps: Step[];
  dialog: boolean;
  dock: boolean;
  outcome?: Outcome;
  issued: boolean;
  error?: string;
}

interface ActivityApi {
  jobs: Job[];
  track: (input: ActivityInput, run: (apply: (event: Record<string, unknown>) => void, signal: AbortSignal) => Promise<void>) => Promise<boolean>;
  open: (id: number) => void;
}

const ActivityContext = createContext<ActivityApi | null>(null);

export function useActivity(): ActivityApi {
  const value = useContext(ActivityContext);
  if (!value) throw new Error('Activity is not mounted');
  return value;
}

function eventLine(event: Record<string, unknown>): string {
  if (typeof event.detail === 'string' && event.detail.trim()) return event.detail.trim();
  if (typeof event.error === 'string' && event.error.trim()) return event.error.trim();
  if (typeof event.step === 'string' && typeof event.state === 'string') return `${event.step} ${event.state}`;
  if (event.outcome === 'sent') return 'Sent';
  if (event.outcome === 'failed' || event.outcome === 'pending') return String(event.outcome);
  return '';
}

function applyEvent(job: Job, event: Record<string, unknown>): Job {
  const step = typeof event.step === 'string' ? event.step : '';
  const state = event.state === 'active' || event.state === 'done' || event.state === 'failed' ? event.state : '';
  const next = { ...job, steps: job.steps.map((item) => ({ ...item })) };
  if (step && state) {
    next.steps = next.steps.map((item) => item.id === step ? { ...item, state, detail: typeof event.detail === 'string' ? event.detail : item.detail } : item);
  }
  if (event.outcome === 'sent' || event.outcome === 'failed' || event.outcome === 'pending') {
    next.outcome = event.outcome;
    next.issued = event.issued === true;
    next.error = typeof event.error === 'string' ? event.error : undefined;
    next.dock = true;
  }
  return next;
}

export function ActivityProvider({ children }: { children: ReactNode }) {
  const notice = useNotice();
  const [jobs, setJobs] = useState<Job[]>([]);
  const latest = useRef(new Map<number, Job>());
  const runners = useRef(new Map<number, (apply: (event: Record<string, unknown>) => void, signal: AbortSignal) => Promise<void>>());
  const aborts = useRef(new Map<number, AbortController>());
  const seq = useRef(0);

  const patch = useCallback((id: number, update: (job: Job) => Job) => {
    setJobs((current) => current.map((job) => {
      if (job.id !== id) return job;
      const next = update(job);
      latest.current.set(id, next);
      return next;
    }));
  }, []);

  const execute = useCallback(async (id: number, title: string, run: (apply: (event: Record<string, unknown>) => void, signal: AbortSignal) => Promise<void>) => {
    const proc = notice.openProcess(title);
    const apply = (event: Record<string, unknown>) => {
      patch(id, (job) => applyEvent(job, event));
      const line = eventLine(event);
      if (line) proc.log(line);
      if (event.outcome === 'sent') proc.finish('done');
      else if (event.outcome === 'failed' || event.outcome === 'pending') proc.finish(event.error === 'Cancelled' ? 'cancelled' : 'failed', typeof event.error === 'string' ? event.error : undefined);
    };
    const controller = new AbortController();
    aborts.current.set(id, controller);
    proc.bindCancel(() => controller.abort());
    try {
      await run(apply, controller.signal);
      proc.finish('done');
    } catch (err) {
      const issued = latest.current.get(id)?.issued === true;
      if (controller.signal.aborted) apply({ outcome: 'failed', issued, error: 'Cancelled' });
      else apply({ outcome: 'failed', issued: false, error: apiError(err, 'That failed') });
    } finally {
      aborts.current.delete(id);
    }
  }, [notice, patch]);

  const track = useCallback(async (input: ActivityInput, run: (apply: (event: Record<string, unknown>) => void, signal: AbortSignal) => Promise<void>) => {
    if (!input.confirmed) {
      const ok = await notice.confirm(input.title, `${input.subtitle} Proceed sends it. Cancel leaves it unsent.`);
      if (!ok) return false;
    }
    const id = ++seq.current;
    runners.current.set(id, run);
    const job: Job = {
      ...input,
      id,
      steps: input.steps.map((step, index) => ({ ...step, state: index === 0 ? 'active' : 'wait' })),
      dialog: true,
      dock: false,
      issued: false,
    };
    latest.current.set(id, job);
    setJobs((current) => [...current, job]);
    await execute(id, input.title, run);
    return latest.current.get(id)?.issued === true;
  }, [execute, notice]);

  const open = useCallback((id: number) => patch(id, (job) => ({ ...job, dialog: true })), [patch]);
  const api = useMemo(() => ({ jobs, track, open }), [jobs, track, open]);
  const dialog = jobs.find((job) => job.dialog);

  return (
    <ActivityContext.Provider value={api}>
      {children}
      {dialog ? (
        <SendDialog
          job={dialog}
          onClose={() => patch(dialog.id, (job) => ({ ...job, dialog: false, dock: true }))}
          onDone={() => setJobs((current) => current.filter((job) => job.id !== dialog.id))}
          onCancel={() => {
            aborts.current.get(dialog.id)?.abort();
            patch(dialog.id, (job) => applyEvent(job, { outcome: 'failed', issued: job.issued, error: 'Cancelled' }));
          }}
          onRetry={() => {
            const run = runners.current.get(dialog.id);
            if (!run) return;
            patch(dialog.id, (job) => ({
              ...job,
              outcome: undefined,
              error: undefined,
              steps: job.steps.map((step, index) => ({ ...step, state: index === 0 ? 'active' : 'wait' })),
            }));
            void execute(dialog.id, dialog.title, run);
          }}
        />
      ) : null}
      <ActivityDock
        jobs={jobs.filter((job) => job.dock && !job.dialog)}
        onOpen={open}
        onDismiss={(id) => patch(id, (job) => ({ ...job, dock: false }))}
      />
    </ActivityContext.Provider>
  );
}

function StepMark({ state }: { state: StepState }) {
  if (state === 'done') return <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white"><Check className="h-3.5 w-3.5" /></span>;
  if (state === 'active') return <span className="bo-spinner" aria-hidden="true" />;
  if (state === 'failed') return <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-xs font-semibold text-white">!</span>;
  return <span className="h-6 w-6 rounded-full border border-[var(--bo-border)]" />;
}

function SendDialog({ job, onClose, onDone, onRetry, onCancel }: { job: Job; onClose: () => void; onDone: () => void; onRetry: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-[75] flex items-end bg-gray-900/50 md:items-center md:justify-center md:p-4" onMouseDown={(event) => { event.stopPropagation(); if (event.target === event.currentTarget) onClose(); }}>
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-2xl border border-[var(--bo-border)] bg-[var(--bo-surface)] shadow-2xl md:rounded-2xl" role="dialog" aria-modal="true" aria-labelledby="send-progress-title">
        <div className="flex items-start justify-between gap-3 px-6 pt-5">
          <div className="flex items-start gap-3">
            {job.outcome ? null : <span className="bo-spinner mt-1" aria-hidden="true" />}
            <div>
              <h3 id="send-progress-title" className="text-lg font-semibold">{job.outcome ? outcomeTitle(job.outcome) : job.title}</h3>
              <p className="text-sm text-[var(--bo-muted)]">{job.outcome ? outcomeDetail(job) : job.subtitle}</p>
            </div>
          </div>
          <button type="button" aria-label="Close" className="shrink-0 rounded-lg p-1 text-[var(--bo-muted)] hover:bg-[var(--bo-hover)]" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        {job.to ? <p className="px-6 pt-4 text-sm"><span className="text-[var(--bo-muted)]">To: </span>{job.to}</p> : null}
        <div className="px-6 py-4">
          {job.outcome ? <OutcomeBody job={job} /> : (
            <ol className="space-y-4">
              {job.steps.map((step) => (
                <li key={step.id} className="flex gap-3">
                  <StepMark state={step.state} />
                  <div>
                    <div className="text-sm font-medium">{step.label}</div>
                    {step.detail && step.state !== 'wait' ? <div className="text-sm text-[var(--bo-muted)]">{step.detail}</div> : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
        {job.outcome ? (
          <div className="flex flex-col gap-2 border-t border-[var(--bo-border)] px-4 py-4 md:flex-row md:justify-end md:px-6">
            {job.outcome === 'failed' && job.issued ? <Button onClick={onRetry}>Retry sending</Button> : null}
            <Button variant={job.outcome === 'failed' && job.issued ? 'outline' : 'default'} onClick={onDone}>Done</Button>
          </div>
        ) : (
          <div className="space-y-4 px-6 pb-5">
            <div className="flex gap-2 rounded-lg border border-[var(--bo-border)] px-3 py-2 text-sm text-[var(--bo-muted)]">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>You can close this window. Sending will continue in the background.</span>
            </div>
            <div className="flex flex-col gap-2 md:flex-row md:justify-end">
              <Button variant="outline" onClick={onCancel}>Cancel</Button>
              <Button onClick={onClose}>Run in background</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function outcomeTitle(outcome: Outcome): string {
  if (outcome === 'sent') return 'Email sent';
  if (outcome === 'failed') return 'That did not finish';
  return 'Checking send status';
}

function outcomeDetail(job: Job): string {
  if (job.outcome === 'sent') return 'The mail service accepted your message.';
  if (job.outcome === 'failed') return job.error || 'The request failed.';
  return 'We have not received confirmation yet. Your message may already have been sent.';
}

function OutcomeBody({ job }: { job: Job }) {
  const tone = job.outcome === 'sent' ? 'text-emerald-400' : 'text-amber-400';
  const kind = job.kindLabel || 'Offer';
  const label = job.outcome === 'sent' ? `${kind} issued · Email sent` : job.issued ? `${kind} issued · Send failed` : 'Send failed';
  return (
    <div className="space-y-3 text-sm">
      <div className={`rounded-lg border border-[var(--bo-border)] px-3 py-2 ${tone}`}>{label}</div>
      {job.to ? <div className="flex justify-between gap-3"><span className="text-[var(--bo-muted)]">To</span><span>{job.to}</span></div> : null}
      {job.attachments ? <div className="flex justify-between gap-3"><span className="text-[var(--bo-muted)]">Attachments</span><span>{job.attachments}</span></div> : null}
      {job.revision ? <div className="flex justify-between gap-3"><span className="text-[var(--bo-muted)]">Revision</span><span>{job.revision}</span></div> : null}
      <p className="text-[var(--bo-muted)]">{job.outcome === 'sent' ? 'Delivery to the recipient is not yet confirmed.' : (job.error || (job.issued ? 'Your message and attachments are ready to retry.' : ''))}</p>
    </div>
  );
}

function ActivityDock({ jobs, onOpen, onDismiss }: { jobs: Job[]; onOpen: (id: number) => void; onDismiss: (id: number) => void }) {
  if (!jobs.length) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[65] flex w-full max-w-md flex-col gap-2">
      {jobs.map((job) => {
        const done = job.outcome === 'sent';
        const failed = job.outcome === 'failed';
        return (
          <div key={job.id} className="flex items-center gap-3 rounded-xl border border-[var(--bo-border)] bg-[var(--bo-surface)] px-4 py-3 shadow-lg">
            {done ? <Check className="h-4 w-4 text-emerald-400" /> : failed ? <span className="text-amber-400">!</span> : <Loader2 className="h-4 w-4 animate-spin text-[#895CF6]" />}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{done ? 'Email sent' : failed ? 'Email was not sent' : job.dockTitle}</div>
              <div className="truncate text-xs text-[var(--bo-muted)]">{job.attachments || job.subtitle}</div>
            </div>
            <button type="button" className="text-sm text-[#895CF6]" onClick={() => onOpen(job.id)}>View activity</button>
            <button type="button" aria-label="Dismiss" className="text-[var(--bo-muted)]" onClick={() => onDismiss(job.id)}><X className="h-4 w-4" /></button>
          </div>
        );
      })}
    </div>
  );
}
